(function () {
  'use strict';

  // Double-injection guard — a merchant copying the script tag manually, or a
  // theme that injects assets more than once, must not trigger a re-define error.
  if (customElements.get('megamenu-bar')) return;

  /* ================================================================
     Utilities
     ================================================================ */

  // gidTail and allFocusable are used in sub-tasks 3 and 4 respectively.
  // eslint-disable-next-line no-unused-vars
  function gidTail(gid) {
    if (typeof gid !== 'string') return '';
    var parts = gid.split('/');
    return parts[parts.length - 1] || '';
  }

  /**
   * Returns the first focusable descendant of `el`, or null.
   */
  function firstFocusable(el) {
    return el.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  // eslint-disable-next-line no-unused-vars
  function allFocusable(el) {
    return Array.from(
      el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  /* ================================================================
     Animation helpers
     ================================================================ */

  // Module-level MediaQueryList for reduced-motion, lazily initialised in
  // MegamenuBar.connectedCallback (and on first read if the bar hasn't
  // connected yet). Test mode can override via window.__megamenuTest.setReducedMotion.
  var _reducedMotionMQ = null;
  var _reducedMotionOverride = null; // null = not overridden; true/false = forced

  function prefersReducedMotion() {
    if (_reducedMotionOverride !== null) return _reducedMotionOverride;
    if (!_reducedMotionMQ) {
      // Lazy init — safe to call before bar connects; falls back to false in
      // environments (e.g. old JSDOM) that don't implement matchMedia.
      if (typeof window !== 'undefined' && window.matchMedia) {
        try {
          _reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
        } catch (e) {
          return false;
        }
      } else {
        return false;
      }
    }
    return _reducedMotionMQ ? _reducedMotionMQ.matches : false;
  }

  /**
   * Show `el` with a CSS transition:
   *   1. Remove [hidden] (makes display: block — but FROM-state styles apply).
   *   2. Force a reflow so the browser locks in the FROM-state paint frame.
   *   3. rAF: add `openClass` — browser interpolates FROM → TO.
   *
   * When reduced motion is preferred, the toggle is synchronous.
   *
   * @param {HTMLElement} el
   * @param {string} openClass
   * @param {object} [controller] - `{ cancel }` cancel handle; populated in-place if provided.
   */
  function showWithTransition(el, openClass, controller) {
    if (prefersReducedMotion()) {
      el.removeAttribute('hidden');
      el.classList.add(openClass);
      return;
    }
    el.removeAttribute('hidden');
    // Force reflow — locks in the "from" frame so the browser sees a
    // paint-ready state before the rAF callback adds the open class.
    // This is load-bearing: without it the browser may batch the two writes
    // into the same frame and skip the transition entirely.
    // eslint-disable-next-line no-unused-expressions
    void el.offsetWidth;
    var rafId = requestAnimationFrame(function () {
      // Mobile panel: measure scrollHeight inside the rAF (layout is settled
      // after [hidden] removal above). Set as --mm-mobile-panel-max-height so
      // the CSS transition has a real numeric TO-value. Falls back to 80vh if
      // scrollHeight returns 0 (e.g. panel has no rendered content yet).
      if (el.tagName && el.tagName.toLowerCase() === 'megamenu-panel' &&
          el.closest && el.closest('.megamenu-bar--mobile')) {
        var h = el.scrollHeight;
        el.style.setProperty(
          '--mm-mobile-panel-max-height',
          h > 0 ? h + 'px' : '80vh'
        );
      }
      el.classList.add(openClass);
      if (controller) controller._rafId = null;
    });
    if (controller) controller._rafId = rafId;
  }

  /**
   * Hide `el` after its CSS transition finishes:
   *   1. Remove `openClass` — browser plays the transition TO the FROM state.
   *   2. Listen for `transitionend` (or fall back to a 600 ms timeout) then
   *      add [hidden] and call `onDone`.
   *
   * When reduced motion is preferred, the toggle is synchronous.
   *
   * @param {HTMLElement} el
   * @param {string} openClass
   * @param {function} [onDone] - Called after [hidden] is re-applied.
   * @param {object} [controller] - `{ cancel }` handle; populated in-place if provided.
   */
  function hideWithTransition(el, openClass, onDone, controller) {
    if (prefersReducedMotion()) {
      el.classList.remove(openClass);
      el.setAttribute('hidden', '');
      if (onDone) onDone();
      return;
    }
    el.classList.remove(openClass);

    var cancelled = false;
    var timer = null;

    var cleanup = function () {
      el.removeEventListener('transitionend', onEnd);
      if (timer !== null) { clearTimeout(timer); timer = null; }
    };

    var done = function () {
      if (cancelled) return;
      cleanup();
      el.setAttribute('hidden', '');
      if (onDone) onDone();
    };

    var onEnd = function (event) {
      if (event.target !== el) return;
      var prop = event.propertyName;
      // Accept any of the properties we transition; the first one fires done().
      if (prop !== 'opacity' && prop !== 'transform' && prop !== 'height' && prop !== 'max-block-size') return;
      done();
    };

    el.addEventListener('transitionend', onEnd);
    // 600 ms safety net — fires if transitionend never arrives (e.g. anim-none,
    // or element removed from DOM mid-transition).
    timer = setTimeout(done, 600);

    if (controller) {
      controller.cancel = function () {
        cancelled = true;
        cleanup();
      };
    }
  }

  /* ================================================================
     MegamenuPanel  (<megamenu-panel>)
     ================================================================ */

  /**
   * Receives open() / close() calls from MegamenuBar.
   * Owns:
   *   - [hidden] toggle + --open class
   *   - megamenu:open / megamenu:close custom events
   *   - focus trap when opened via click (TODO sub-task 3)
   *
   * TODO sub-task 4: cascading-sidebar pane swap — connectedCallback
   */
  class MegamenuPanel extends HTMLElement {
    constructor() {
      super();
      this._triggerEl = null;    // bar item that last opened this panel
      this._openedViaClick = false;
      this._trapHandler = null;  // bound keydown for focus trap
      // In-flight transition controller. Cancelled by the opposing call so
      // rapid hover-in/out doesn't apply [hidden] after a second hover-in.
      this._transition = null;
    }

    connectedCallback() {
      if (this.classList.contains('megamenu-panel--cascading-sidebar')) {
        this._initCascading();
      }
      if (this.classList.contains('megamenu-panel--simple-dropdown')) {
        this._initSimpleDropdown();
      }
    }

    disconnectedCallback() {
      this._removeFocusTrap();
      this._teardownCascading();
      this._teardownSimpleDropdown();
    }

    /**
     * Open the panel.
     * @param {HTMLElement} [triggerEl] - The bar item that triggered the open.
     * @param {boolean} [viaClick] - True when opened by explicit click (activates focus trap).
     */
    open(triggerEl, viaClick) {
      // Cancel any in-flight close transition so it doesn't apply [hidden]
      // after this open completes (rapid hover-out then hover-in scenario).
      if (this._transition) {
        if (this._transition._rafId !== null && this._transition._rafId !== undefined) {
          cancelAnimationFrame(this._transition._rafId);
        }
        if (this._transition.cancel) this._transition.cancel();
        this._transition = null;
      }

      this._triggerEl = triggerEl || null;
      this._openedViaClick = Boolean(viaClick);

      // Update triggering bar link aria-expanded immediately on open intent.
      if (triggerEl) {
        var link = triggerEl.querySelector('.megamenu-bar__link');
        if (link) link.setAttribute('aria-expanded', 'true');
      }

      // TODO sub-task 3: activate focus trap when _openedViaClick is true
      // this._installFocusTrap();

      // megamenu:open fires immediately on open intent, before the visual
      // transition begins. Listeners expecting this event at trigger time
      // (e.g. analytics, bar state machine) are not affected.
      this.dispatchEvent(new CustomEvent('megamenu:open', {
        bubbles: true,
        composed: true,
        detail: { panel: this, trigger: triggerEl }
      }));

      var controller = {};
      this._transition = controller;
      showWithTransition(this, 'megamenu-panel--open', controller);
    }

    /**
     * Close the panel.
     * @param {boolean} [returnFocus] - If true, re-focus the triggering bar item.
     */
    close(returnFocus) {
      // Cancel any in-flight open rAF so it doesn't add --open after this
      // close starts (rapid hover-in then hover-out scenario).
      if (this._transition) {
        if (this._transition._rafId !== null && this._transition._rafId !== undefined) {
          cancelAnimationFrame(this._transition._rafId);
        }
        if (this._transition.cancel) this._transition.cancel();
        this._transition = null;
      }

      var trigger = this._triggerEl;
      this._openedViaClick = false;
      this._triggerEl = null;

      // aria-expanded flips immediately on close intent — keyboard handlers
      // (Escape, ArrowLeft/Right) must see the correct state straight away
      // even though the visual panel is still fading out.
      if (trigger) {
        var link = trigger.querySelector('.megamenu-bar__link');
        if (link) link.setAttribute('aria-expanded', 'false');
      }

      this._removeFocusTrap();

      var self = this;
      var onClosed = function () {
        self._transition = null;
        // megamenu:close fires AFTER the transition completes so external
        // listeners that re-render the bar (e.g. analytics, parent state)
        // don't yank the DOM mid-fade. This is a deliberate semantic shift
        // from the pre-animation synchronous behaviour.
        self.dispatchEvent(new CustomEvent('megamenu:close', {
          bubbles: true,
          composed: true,
          detail: { panel: self }
        }));

        if (returnFocus && trigger) {
          var focusTarget = trigger.querySelector('.megamenu-bar__link') || trigger;
          focusTarget.focus({ preventScroll: true });
        }
      };

      var controller = {};
      this._transition = controller;
      hideWithTransition(this, 'megamenu-panel--open', onClosed, controller);
    }

    /** Whether the panel is currently visible. */
    get isOpen() {
      return !this.hasAttribute('hidden');
    }

    /* ---- Focus trap (activated on click-open, sub-task 3) ---- */

    _installFocusTrap() {
      // TODO sub-task 3: implement Tab/Shift+Tab focus trap
      // When this.classList.contains open, Tab/Shift+Tab should cycle
      // focus within allFocusable(this). On Escape, call close(true).
    }

    _removeFocusTrap() {
      if (this._trapHandler) {
        this.removeEventListener('keydown', this._trapHandler);
        this._trapHandler = null;
      }
    }

    /* ---- Cascading sidebar pane swap ---- */

    /**
     * Wire sidebar buttons so hovering or clicking swaps the active pane.
     * Called from connectedCallback when the panel has --cascading-sidebar.
     *
     * Interaction contract:
     *   pointerenter / click → activate target sidebar item + show matching pane
     *   Escape inside sidebar → propagates up to MegamenuBar's Escape handler (no preventDefault)
     *   Tab from sidebar button → natural tab order enters the active pane;
     *     [hidden] removes hidden panes from tab order automatically.
     */
    _initCascading() {
      var self = this;
      self._cascadingButtons = Array.from(
        self.querySelectorAll('.megamenu-sidebar__item')
      );

      self._cascadeHandler = function (event) {
        var btn = event.currentTarget;
        var targetId = btn.dataset.cascadeTarget;
        if (!targetId) return;
        self._activateCascadePane(targetId);
      };

      self._cascadingButtons.forEach(function (btn) {
        btn.addEventListener('pointerenter', self._cascadeHandler);
        btn.addEventListener('click',        self._cascadeHandler);
      });
    }

    /**
     * Show the pane matching `targetId`, hide all others.
     * Updates aria-current and --active class on sidebar buttons.
     */
    _activateCascadePane(targetId) {
      var self = this;

      // Update sidebar button states
      self._cascadingButtons.forEach(function (btn) {
        var isTarget = btn.dataset.cascadeTarget === targetId;
        btn.classList.toggle('megamenu-sidebar__item--active', isTarget);
        if (isTarget) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
      });

      // Show/hide panes
      var panes = self.querySelectorAll('.megamenu-cascade-pane');
      panes.forEach(function (pane) {
        if (pane.dataset.cascadePane === targetId) {
          pane.removeAttribute('hidden');
        } else {
          pane.setAttribute('hidden', '');
        }
      });
    }

    /** Remove cascading sidebar listeners (called from disconnectedCallback). */
    _teardownCascading() {
      if (!this._cascadingButtons) return;
      var self = this;
      self._cascadingButtons.forEach(function (btn) {
        btn.removeEventListener('pointerenter', self._cascadeHandler);
        btn.removeEventListener('click',        self._cascadeHandler);
      });
      self._cascadingButtons = null;
      self._cascadeHandler   = null;
    }

    /* ---- Simple dropdown accordion ---- */

    /**
     * Wire accordion toggle buttons for the simple-dropdown panel layout.
     * Each L2 with children gets a toggle button; only one section is open
     * at a time. In hover trigger mode, pointerenter on the row also opens it.
     * Called from connectedCallback when the panel has --simple-dropdown.
     */
    _initSimpleDropdown() {
      var self = this;
      var simple = self.querySelector('.megamenu-simple');
      if (!simple) return;

      var triggerMode = simple.dataset.megamenuTrigger || 'hover';
      var triggers = Array.from(simple.querySelectorAll('[data-megamenu-submenu-trigger]'));

      var clickHandler = function (event) {
        var btn = event.currentTarget;
        var willExpand = btn.getAttribute('aria-expanded') !== 'true';
        self._setSimpleAccordion(btn, willExpand);
      };
      triggers.forEach(function (btn) {
        btn.addEventListener('click', clickHandler);
      });

      var rowEnterHandlers = [];
      if (triggerMode === 'hover') {
        triggers.forEach(function (btn) {
          var row = btn.closest('.megamenu-section--collapsible');
          if (!row) return;
          var fn = function () { self._setSimpleAccordion(btn, true); };
          row.addEventListener('pointerenter', fn);
          rowEnterHandlers.push({ el: row, fn: fn });
        });
      }

      self._simpleAccordion = {
        container: simple,
        triggerMode: triggerMode,
        triggers: triggers,
        clickHandler: clickHandler,
        rowEnterHandlers: rowEnterHandlers
      };
    }

    /**
     * Expand or collapse a single accordion section.
     * When expanding, closes all other open sections first (single-open).
     * @param {HTMLElement} btn - The toggle button for the target section.
     * @param {boolean} expand  - True to open, false to close.
     */
    _setSimpleAccordion(btn, expand) {
      var state = this._simpleAccordion;
      if (!state) return;

      if (expand) {
        state.triggers.forEach(function (other) {
          if (other === btn) return;
          var pid = other.getAttribute('aria-controls');
          var p = pid ? state.container.querySelector('#' + pid) : null;
          other.setAttribute('aria-expanded', 'false');
          if (p) hideWithTransition(p, 'megamenu-section-links--open');
        });
      }
      btn.setAttribute('aria-expanded', expand ? 'true' : 'false');
      var pid = btn.getAttribute('aria-controls');
      var panel = pid ? state.container.querySelector('#' + pid) : null;
      if (panel) {
        if (expand) {
          // Remove [hidden] temporarily to measure scrollHeight, then re-hide
          // before handing off to showWithTransition which will do its own
          // remove-then-reflow-then-rAF cycle. This avoids a flash of content.
          if (panel.hasAttribute('hidden')) {
            panel.removeAttribute('hidden');
            var sh = panel.scrollHeight;
            panel.setAttribute('hidden', '');
            if (sh > 0) panel.style.setProperty('--mm-accordion-max-height', sh + 'px');
          }
          showWithTransition(panel, 'megamenu-section-links--open');
        } else {
          // Reset the max-height variable so the next open remeasures fresh content.
          panel.style.removeProperty('--mm-accordion-max-height');
          hideWithTransition(panel, 'megamenu-section-links--open');
        }
      }
    }

    /** Remove simple dropdown listeners (called from disconnectedCallback). */
    _teardownSimpleDropdown() {
      var state = this._simpleAccordion;
      if (!state) return;
      state.triggers.forEach(function (btn) {
        btn.removeEventListener('click', state.clickHandler);
      });
      state.rowEnterHandlers.forEach(function (h) {
        h.el.removeEventListener('pointerenter', h.fn);
      });
      this._simpleAccordion = null;
    }
  }

  /* ================================================================
     MegamenuBar  (<megamenu-bar>)
     ================================================================ */

  /**
   * Top-level controller for the megamenu bar.
   *
   * State machine (per bar, one panel open at a time):
   *   closed → hover-pending-open → open → hover-pending-close → closed
   *   closed → open (click trigger)
   *
   * Attributes read at connectedCallback:
   *   data-hover-open-delay  (ms, default 100)
   *   data-hover-close-delay (ms, default 200)
   *
   * isMobile getter: true when matchMedia('(max-width: 768px)').matches.
   * Mobile behavior changes (detach hover, switch to click accordion)
   * are wired in sub-task 5.
   */
  class MegamenuBar extends HTMLElement {
    constructor() {
      super();

      // State: 'closed' | 'hover-pending-open' | 'open' | 'hover-pending-close'
      this._state = 'closed';
      this._openItem    = null;       // <li> item currently open
      this._currentPanel = null;      // <megamenu-panel> currently open
      this._hoverTimer = null;

      this._hoverOpenDelay = 100;
      this._hoverCloseDelay = 200;

      this._mq = null;             // MediaQueryList for mobile breakpoint
      this._mqHandler = null;

      // Bound handlers stored for removal
      this._onDocClick = this._handleDocumentClick.bind(this);
      this._onKeydown  = this._handleKeydown.bind(this);
      this._onItemPointerEnter = this._handleItemPointerEnter.bind(this);
      this._onItemPointerLeave = this._handleItemPointerLeave.bind(this);
      this._onItemClick = this._handleItemClick.bind(this);
    }

    connectedCallback() {
      this._hoverOpenDelay  = parseInt(this.dataset.hoverOpenDelay  || '100', 10);
      this._hoverCloseDelay = parseInt(this.dataset.hoverCloseDelay || '200', 10);

      // Cache the reduced-motion MediaQueryList at IIFE scope so prefersReducedMotion()
      // can read it without re-querying matchMedia on every call.
      if (!_reducedMotionMQ && window.matchMedia) {
        try {
          _reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
        } catch (e) { /* non-fatal — prefersReducedMotion falls back to false */ }
      }

      // Mobile breakpoint detection — apply immediately, then watch for changes
      this._mq = window.matchMedia('(max-width: 768px)');
      this._mqHandler = this._handleBreakpointChange.bind(this);
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._mqHandler);
      } else {
        // Safari <14 fallback
        this._mq.addListener(this._mqHandler);
      }

      // Wire item listeners (hover for desktop, click always)
      this._attachItemListeners();

      // Apply initial mobile state if already in a narrow viewport
      if (this.isMobile) {
        this.classList.add('megamenu-bar--mobile');
      }

      document.addEventListener('click', this._onDocClick, { capture: false });
      this.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      this._clearTimer();
      document.removeEventListener('click', this._onDocClick, { capture: false });
      this.removeEventListener('keydown', this._onKeydown);
      this._detachItemListeners();

      if (this._mq) {
        if (this._mq.removeEventListener) {
          this._mq.removeEventListener('change', this._mqHandler);
        } else {
          this._mq.removeListener(this._mqHandler);
        }
      }
    }

    /* ---- Public getter ---- */

    /** True when viewport is ≤768px (mobile). Behavior changes wired in sub-task 5. */
    get isMobile() {
      return this._mq ? this._mq.matches : false;
    }

    /* ================================================================
       Listener wiring
       ================================================================ */

    _getItems() {
      return Array.from(this.querySelectorAll(':scope > .megamenu-bar__items > .megamenu-bar__item'));
    }

    _attachItemListeners() {
      this._getItems().forEach(function (item) {
        var link = item.querySelector('.megamenu-bar__link');
        if (!link) return;

        var trigger = link.dataset.megamenuTrigger || 'hover';

        if (trigger === 'hover') {
          item.addEventListener('pointerenter', this._onItemPointerEnter);
          item.addEventListener('pointerleave', this._onItemPointerLeave);
        }
        // click trigger always wired (hover items also respond to click for a11y)
        link.addEventListener('click', this._onItemClick);
      }, this);
    }

    _detachItemListeners() {
      this._getItems().forEach(function (item) {
        item.removeEventListener('pointerenter', this._onItemPointerEnter);
        item.removeEventListener('pointerleave', this._onItemPointerLeave);
        var link = item.querySelector('.megamenu-bar__link');
        if (link) link.removeEventListener('click', this._onItemClick);
      }, this);
    }

    /* ================================================================
       Event handlers
       ================================================================ */

    _handleItemPointerEnter(event) {
      if (this.isMobile) return;

      var item = event.currentTarget;
      var panel = this._panelFor(item);
      if (!panel) return; // plain link, no panel

      this._clearTimer();

      if ((this._state === 'open' || this._state === 'hover-pending-close')
          && this._openItem
          && this._openItem !== item) {
        // Switching directly from one open (or pending-close) item to another
        this._closePanel(false);
        this._state = 'open';
        this._openPanelFor(item, panel, false);
        return;
      }

      if (this._state === 'closed' || this._state === 'hover-pending-close') {
        this._state = 'hover-pending-open';
        this._hoverTimer = setTimeout(function () {
          this._state = 'open';
          this._openPanelFor(item, panel, false);
        }.bind(this), this._hoverOpenDelay);
      }
    }

    _handleItemPointerLeave(event) {
      if (this.isMobile) return;

      // Only trigger close if we're leaving to outside the bar
      var related = event.relatedTarget;
      var panel = this._currentPanel;

      // If moving into the open panel, cancel close
      if (panel && related && panel.contains(related)) return;

      this._clearTimer();

      if (this._state === 'open' || this._state === 'hover-pending-open') {
        this._state = 'hover-pending-close';
        this._hoverTimer = setTimeout(function () {
          if (this._state === 'hover-pending-close') {
            this._closeCurrent(false);
          }
        }.bind(this), this._hoverCloseDelay);
      }
    }

    _handleItemClick(event) {
      var link = event.currentTarget;
      var item = link.closest('.megamenu-bar__item');
      if (!item) return;

      var panel = this._panelFor(item);
      if (!panel) return; // plain link — let it navigate

      event.preventDefault();

      // Mobile: use accordion toggle (outside-click suppressed; each item independent)
      if (this.isMobile) {
        this._toggleMobile(item);
        return;
      }

      // Desktop: existing click-trigger logic
      this._clearTimer();

      if (this._state === 'open' && this._openItem === item) {
        // Second click — close
        this._closeCurrent(true);
      } else {
        if (this._state === 'open') {
          this._closePanel(false);
        }
        this._state = 'open';
        this._openPanelFor(item, panel, true);
      }
    }

    _handleDocumentClick(event) {
      if (this._state !== 'open') return;

      // Close if click landed outside the bar and outside the open panel
      var insideBar   = this.contains(event.target);
      var insidePanel = this._currentPanel && this._currentPanel.contains(event.target);

      if (!insideBar && !insidePanel) {
        this._clearTimer();
        this._closeCurrent(false);
      }
    }

    _handleKeydown(event) {
      var key = event.key;
      var items = this._getItems();

      /* ---- Escape ---- */
      if (key === 'Escape') {
        if (this._state === 'open') {
          event.preventDefault();
          this._closeCurrent(true); // return focus to trigger
        }
        return;
      }

      /* ---- Arrow keys along bar (WAI-ARIA Menubar) ---- */
      var focused = document.activeElement;
      var focusedItem = focused ? focused.closest('.megamenu-bar__item') : null;
      var idx = focusedItem ? items.indexOf(focusedItem) : -1;

      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        event.preventDefault();
        var dir = key === 'ArrowRight' ? 1 : -1;
        var next = items[(idx + dir + items.length) % items.length];
        if (next) {
          var nextLink = next.querySelector('.megamenu-bar__link');
          if (nextLink) nextLink.focus({ preventScroll: true });
        }
        return;
      }

      /* ---- ArrowDown — move focus into open panel ---- */
      if (key === 'ArrowDown' && this._state === 'open' && this._currentPanel) {
        event.preventDefault();
        var first = firstFocusable(this._currentPanel);
        if (first) first.focus({ preventScroll: true });
        return;
      }

      /* ---- Enter / Space on a bar link with panel ---- */
      if ((key === 'Enter' || key === ' ') && focused && focused.classList.contains('megamenu-bar__link')) {
        var link = focused;
        if (link.hasAttribute('aria-haspopup')) {
          event.preventDefault();
          link.click();
        }
      }
    }

    _handleBreakpointChange(event) {
      var isMobile = event ? event.matches : this.isMobile;
      this._applyBreakpoint(isMobile);
    }

    /**
     * Switch between desktop (hover-intent state machine) and mobile (click accordion).
     * @param {boolean} isMobile
     */
    _applyBreakpoint(isMobile) {
      // Close any open panel before switching modes
      if (this._state === 'open') {
        this._closeCurrent(false);
      }
      // Also collapse any mobile-expanded items
      this._getItems().forEach(function (item) {
        item.classList.remove('megamenu-bar__item--mobile-expanded');
        var panel = this._panelFor(item);
        if (panel && panel.isOpen) panel.close(false);
      }, this);

      this._clearTimer();

      if (isMobile) {
        // Add mobile marker class for CSS targeting
        this.classList.add('megamenu-bar--mobile');
        // Detach hover listeners from all items — isMobile getter already short-
        // circuits the handlers, but removing them prevents unnecessary work.
        this._getItems().forEach(function (item) {
          item.removeEventListener('pointerenter', this._onItemPointerEnter);
          item.removeEventListener('pointerleave', this._onItemPointerLeave);
        }, this);
      } else {
        this.classList.remove('megamenu-bar--mobile');
        // Re-attach hover listeners for items that have a hover trigger
        this._getItems().forEach(function (item) {
          var link = item.querySelector('.megamenu-bar__link');
          if (!link) return;
          var trigger = link.dataset.megamenuTrigger || 'hover';
          if (trigger === 'hover') {
            // Avoid double-attach by removing first
            item.removeEventListener('pointerenter', this._onItemPointerEnter);
            item.removeEventListener('pointerleave', this._onItemPointerLeave);
            item.addEventListener('pointerenter', this._onItemPointerEnter);
            item.addEventListener('pointerleave', this._onItemPointerLeave);
          }
        }, this);
      }
    }

    /**
     * Toggle accordion open/close for a single item in mobile mode.
     * Only one item may be expanded at a time.
     * @param {HTMLElement} item - The .megamenu-bar__item <li> element.
     */
    _toggleMobile(item) {
      var panel = this._panelFor(item);
      if (!panel) return;

      var isExpanded = item.classList.contains('megamenu-bar__item--mobile-expanded');

      // Collapse all items first (one open at a time)
      this._getItems().forEach(function (other) {
        if (other === item) return;
        other.classList.remove('megamenu-bar__item--mobile-expanded');
        var otherPanel = this._panelFor(other);
        if (otherPanel && otherPanel.isOpen) otherPanel.close(false);
      }, this);

      if (isExpanded) {
        item.classList.remove('megamenu-bar__item--mobile-expanded');
        panel.close(false);
      } else {
        item.classList.add('megamenu-bar__item--mobile-expanded');
        panel.open(item, false);
      }
    }

    /* ================================================================
       Open / close internals
       ================================================================ */

    _panelFor(item) {
      return item.querySelector('megamenu-panel') || null;
    }

    _openPanelFor(item, panel, viaClick) {
      this._openItem    = item;
      this._currentPanel = panel;
      item.classList.add('megamenu-bar__item--open');

      // Set --megamenu-bar-bottom as the FALLBACK anchor for non-Horizon
      // themes. Horizon publishes --top-row-height on document.body, which
      // tracks the bottom edge of the header's top row (where the megamenu
      // bar lives). The CSS rules use:
      //   inset-block-start: var(--top-row-height, var(--megamenu-bar-bottom, 100%))
      // On Horizon, --top-row-height wins and the panel anchors flush with
      // the bottom of the bar's row. On Dawn / vintage / custom themes that
      // don't publish --top-row-height, this JS-set bar-bottom is the best
      // approximation we have.
      if (
        panel.classList.contains('megamenu-panel--width-full-width') ||
        panel.classList.contains('megamenu-panel--width-container-width')
      ) {
        var rect = this.getBoundingClientRect();
        this.style.setProperty('--megamenu-bar-bottom', rect.bottom + 'px');
        panel.style.setProperty('--megamenu-bar-bottom', rect.bottom + 'px');
      }

      panel.open(item, viaClick);
    }

    _closePanel(returnFocus) {
      if (!this._currentPanel) return;
      var item  = this._openItem;
      var panel = this._currentPanel;

      this._openItem     = null;
      this._currentPanel = null;
      this._state        = 'closed';

      if (item) item.classList.remove('megamenu-bar__item--open');
      panel.close(returnFocus);
    }

    _closeCurrent(returnFocus) {
      this._clearTimer();
      this._closePanel(returnFocus);
    }

    _clearTimer() {
      if (this._hoverTimer !== null) {
        clearTimeout(this._hoverTimer);
        this._hoverTimer = null;
      }
    }
  }

  /* ================================================================
     Registration
     ================================================================ */

  customElements.define('megamenu-panel', MegamenuPanel);
  customElements.define('megamenu-bar',   MegamenuBar);

  /* ================================================================
     Test-mode exports (only when window.__megamenuTestMode is set)
     Exposes internal helpers so Vitest can test them without touching
     the production export surface.
     ================================================================ */
  if (typeof window !== 'undefined' && window.__megamenuTestMode) {
    window.__megamenuTest = {
      gidTail: gidTail,
      MegamenuBar: MegamenuBar,
      MegamenuPanel: MegamenuPanel,
      /**
       * Override prefersReducedMotion() for tests.
       * @param {boolean|null} value - true/false to force, null to clear override.
       *   When null, also clears _reducedMotionMQ so the next read re-queries
       *   matchMedia with the current test stub.
       */
      setReducedMotion: function (value) {
        _reducedMotionOverride = value;
        if (value === null) {
          // Reset the cached MQ so the next prefersReducedMotion() call
          // picks up whatever matchMedia stub the current test has installed.
          _reducedMotionMQ = null;
        }
      },
    };
  }

})();
